import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { useProject } from '../../shared/context/ProjectContext';
import { useReferenceData } from '../../shared/hooks/useReferenceData';
import { ComboBox } from '../../shared/components/ComboBox';
import { FixedListSelect } from '../../shared/components/FixedListSelect';
import { getMaterialSuggestion } from '../../shared/utils/toolMaterialUtils';
import { updateDisposalTool, deleteDisposalTool } from './api/disposalApi';
import ModalShell from '../../shared/components/ModalShell';
import {
  getPartNumbers, addPartNumber, patchPartNumber, deletePartNumber,
  getAssetTags, addAssetTag, patchAssetTag, deleteAssetTag,
} from '../inventory/api/inventoryApi';
import PartNumbersList from '../../shared/components/PartNumbersList';
import AssetTagsList from '../../shared/components/AssetTagsList';
import { comparePartNumbers } from '../../shared/utils/reconSort';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PhotoGalleryPanel from '../../shared/components/PhotoGalleryPanel';

/* ── Default section order ────────────────────────────────────────────── */
const DEFAULT_ORDER = [
  'tool_identification', 'supplier_info', 'recon_status_panel', 'photos', 'location',
  'disposal_flags', 'supply_chain', 'dimensions_weight',
  'part_numbers', 'asset_tags', 'system_fields',
];

function getPanelOrderKey(email) {
  return `tmg_disposal_panel_order_${email || 'default'}`;
}

function loadPanelOrder(email) {
  try {
    const raw = localStorage.getItem(getPanelOrderKey(email));
    if (!raw) return [...DEFAULT_ORDER];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_ORDER];
    // Drop any stale keys no longer in DEFAULT_ORDER, then append any new
    // DEFAULT_ORDER keys not yet in the user's saved order. Preserves any
    // custom rearrangement while seamlessly absorbing schema additions.
    const validParsed = parsed.filter(k => DEFAULT_ORDER.includes(k));
    const missingKeys = DEFAULT_ORDER.filter(k => !validParsed.includes(k));
    return [...validParsed, ...missingKeys];
  } catch { /* ignore */ }
  return [...DEFAULT_ORDER];
}

function savePanelOrder(email, order) {
  try {
    localStorage.setItem(getPanelOrderKey(email), JSON.stringify(order));
  } catch { /* ignore */ }
}

/* ── Format helpers ───────────────────────────────────────────────────── */
function fmtDatetime(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function fmtBool(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

/* ── Sortable Section wrapper ─────────────────────────────────────────── */
function SortableSection({ id, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ddp-section${isDragging ? ' ddp-section-dragging' : ''}`}
    >
      <div className="ddp-section-header">
        <span className="ddp-drag-handle" {...attributes} {...listeners}>&#10495;</span>
        <button
          className="ddp-section-toggle"
          onClick={() => setOpen(!open)}
          type="button"
          style={{ display: 'contents' }}
        >
          <span className="ddp-section-title">{title}</span>
        </button>
        <span className="ddp-section-header-right">
          <button
            className="ddp-section-chevron-btn"
            onClick={() => setOpen(!open)}
            type="button"
          >
            <span className={`ddp-section-chevron ${open ? 'open' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </span>
      </div>
      {open && <div className="ddp-section-body">{children}</div>}
    </div>
  );
}

/* ── OEM badge config ─────────────────────────────────────────────────── */
const OEM_CONFIG = {
  TOYOTA:     { cls: 'dtl-oem-toyota' },
  GM:         { cls: 'dtl-oem-gm' },
  FORD:       { cls: 'dtl-oem-ford' },
  NISSAN:     { cls: 'dtl-oem-nissan' },
  HONDA:      { cls: 'dtl-oem-honda' },
  STELLANTIS: { cls: 'dtl-oem-stellantis' },
};

/* ── Delete refusal: can a retry possibly succeed? ─────────────────────── */
const FUTILE_DELETE_REASONS = new Set(['not_found', 'already_deleted', 'scrap_project']);
const FUTILE_DELETE_STATUSES = new Set([400, 401, 403]);
const STALE_LIST_REASONS = new Set(['not_found', 'already_deleted']);

/**
 * True only when pressing "Delete tool" again CANNOT succeed, so the footer
 * collapses to a single Close (spec §4.2). Same principle as isRetryFutile in
 * FieldSyncSheet.jsx: never offer a control that cannot work.
 *
 * Futility is the positive test; RETRYABLE is the default. 'conflict' (the
 * handler re-reads and re-etags on every call), 500, and a network failure
 * (no .status — fetch rejected before a Response existed, so throwApiError
 * never ran) all fall through to false, as does any shape not listed here.
 * Collapsing on the unrecognised would strand the operator on a transient
 * failure; an extra button they can press twice is the cheaper mistake.
 */
function isDeleteRetryFutile(err) {
  if (!err) return false;
  if (FUTILE_DELETE_REASONS.has(err.body?.reason)) return true;
  if (FUTILE_DELETE_STATUSES.has(err.status)) return true;
  return false;
}

/* ═══════════════════════════════════════════════════════════════════════ */
export default function DisposalDetailPanel({ tool, onToolUpdate, onRefreshCompleteness, onDirtyChange, onToolDeleted }) {
  const { callerEmail, userProfile } = useUser();
  const { currentProject } = useProject();
  const isAdmin = userProfile?.role === 'ADMIN';

  /* ── Resolve siteId — camelCase or snake_case ────────────────────── */
  const toolSiteId = tool?.siteId || tool?.site_id;

  /* ── ReferenceData ────────────────────────────────────────────────── */
  const { options: refOpts, addValue: addRefValue } = useReferenceData(
    ['tool_type', 'material', 'oem', 'supplier_status', 'preliminary_tool_status',
     'site_location', 'detail_location',
     'row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr',
     'inv_oem_pn_status', 'inv_asset_tag_status', 'tier_level',
     'part_number_type', 'tool_type_material_map'],
    callerEmail,
    toolSiteId
  );

  /* ── Local edit state ─────────────────────────────────────────────── */
  const [editedFields, setEditedFields] = useState({});
  // Auto-applied suggestions (e.g., material from tool_type_material_map).
  // Kept separate from editedFields so they do NOT flip the dirty flag, but
  // still merge into the save payload and display in the form.
  const [autoSuggestions, setAutoSuggestions] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [reconCloseError, setReconCloseError] = useState(null);

  /* ── Delete confirm (ADMIN) ───────────────────────────────────────── */
  // Declared above the `if (!tool) return null` guard below — hooks must not
  // sit after an early return.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The thrown Error itself (not just its message): the footer reads
  // .status / .body.reason to decide whether a retry can work.
  const [deleteError, setDeleteError] = useState(null);

  function openDeleteConfirm() {
    setDeleteError(null);
    setConfirmOpen(true);
  }

  // One identity (ModalShell's Escape effect lists onClose in its deps). The
  // `deleting` guard covers all four exits, incl. the header X, which has no
  // prop to disable it.
  const closeDeleteConfirm = useCallback(() => {
    if (deleting) return;
    setConfirmOpen(false);
  }, [deleting]);

  async function handleDeleteConfirm() {
    setDeleting(true);
    setDeleteError(null);
    try {
      // tool.id is the Cosmos doc id; tool.toolId is supplier_tool_id and would 404.
      const result = await deleteDisposalTool(tool.id, tool.siteId);
      // audited:false = the Tools write landed, the ChangeLog row did not. The
      // delete still succeeded, so the success path runs unchanged; the panel
      // unmounts below, so the console is the only witness this component can give.
      // Witnesses: this console line + the backend's [AUDIT-MISS] App Insights row. An on-screen notice was considered and declined — the panel unmounts and no notice surface exists for it to live in.
      if (result && result.audited === false) {
        console.error(`[AUDIT-MISS] delete_tool: tool_id=${tool.id} was deleted but its ChangeLog audit row is missing`);
      }
      // onDirtyChange normally fires from an effect on isDirty, but the panel
      // unmounts in the same batched render when selection moves, so that
      // effect never runs — the page's hasUnsavedChanges would stay true until
      // the next panel mounts and pop a spurious confirm on the next list click.
      // Hence the inline call, before the page moves selection.
      setEditedFields({});
      if (onDirtyChange) onDirtyChange(false);
      setConfirmOpen(false);
      if (onToolDeleted) onToolDeleted(tool.id);
    } catch (err) {
      setDeleteError(err);
    } finally {
      setDeleting(false);
    }
  }

  /* ── Part Numbers state ──────────────────────────────────────────── */
  const [partNumbers, setPartNumbers] = useState([]);
  const [newPN, setNewPN] = useState('');
  const [newPNType, setNewPNType] = useState('TBD');
  const [newPNDesc, setNewPNDesc] = useState('');
  const [pnError, setPnError] = useState(null);
  const addingPNRef = useRef(false);

  /* ── Asset Tags state ────────────────────────────────────────────── */
  const [assetTags, setAssetTags] = useState([]);
  const [newTagVal, setNewTagVal] = useState('');
  const [atError, setAtError] = useState(null);
  const addingTagRef = useRef(false);

  // Display-only sort: a sorted COPY for render. State + handlers stay on the
  // original `partNumbers` (by-id), so stored order is never reordered.
  const sortedPartNumbers = useMemo(
    () => [...partNumbers].sort(comparePartNumbers), [partNumbers]);

  // Reset edited fields when tool changes
  useEffect(() => {
    setEditedFields({});
    setAutoSuggestions({});
    setSaveMsg(null);
    setReconCloseError(null);
  }, [tool?.id]);

  // Notify parent when dirty state changes
  const isDirty = Object.keys(editedFields).length > 0;
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Load part numbers + asset tags when tool changes
  useEffect(() => {
    if (!tool?.id || !callerEmail) return;
    getPartNumbers(tool.id, callerEmail).then(setPartNumbers).catch(() => setPartNumbers([]));
    getAssetTags(tool.id, callerEmail).then(setAssetTags).catch(() => setAssetTags([]));
  }, [tool?.id, callerEmail]);

  function handleFieldChange(key, value) {
    setEditedFields(prev => ({ ...prev, [key]: value }));
    if (saveMsg && saveMsg !== 'success') setSaveMsg(null);
    if (reconCloseError) setReconCloseError(null);
  }

  const displayVal = (key) =>
    key in editedFields ? editedFields[key]
    : key in autoSuggestions ? autoSuggestions[key]
    : tool[key];

  useEffect(() => {
    if (!refOpts.tool_type_material_map) return;
    const suggestion = getMaterialSuggestion(
      displayVal('toolType'),
      displayVal('material'),
      refOpts.tool_type_material_map
    );
    // Write to autoSuggestions, NOT editedFields — auto-applied values must
    // display + save but must NOT flip isDirty (prevents false "unsaved
    // changes" warning on tool switch).
    if (suggestion) setAutoSuggestions(prev => ({ ...prev, material: suggestion }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- material suggestion effect intentionally excludes displayVal and refOpts.tool_type_material_map to prevent infinite loop (effect sets material -> dep change -> re-trigger)
  }, [editedFields.toolType, tool?.toolType]);

  /* ── camelCase→snake_case mapping for save payload ──────────────── */
  const CAMEL_TO_SNAKE = {
    reconStatus: 'recon_status',
    altToolId: 'alt_tool_id',
    toolType: 'tool_type',
    material: 'material',
    oem: 'oem',
    listNumber: 'list_number',
    platform: 'platform',
    partDescription: 'part_description',
    toolDescription: 'tool_description',
    cspc: 'cspc',
    barcode: 'barcode',
    qrCode: 'qr_code',
    supplierStatus: 'supplier_status',
    supplierComments: 'supplier_comments',
    supplierConfirmedDate: 'supplier_confirmed_date',
    supplierConfirmedBy: 'supplier_confirmed_by',
    reconComments: 'recon_comments',
    preliminaryToolStatus: 'preliminary_tool_status',
    approvedScrap: 'approved_scrap',
    siteLocation: 'site_location',
    detailLocation: 'detail_location',
    rowNbr: 'row_nbr',
    stackNbr: 'stack_nbr',
    rackNbr: 'rack_nbr',
    shelfNbr: 'shelf_nbr',
    skidNbr: 'skid_nbr',
    inventoryComments: 'inventory_comments',
    capacityFlag: 'capacity_flag',
    npnFlag: 'npn_flag',
    found: 'found',
    tmgDataComments: 'tmg_data_comments',
    retainedReason: 'retained_reason',
    tierLevel: 'tier_level',
    tierDuns: 'tier_duns',
    shipToLocation: 'ship_to_location',
    lastShipDate: 'last_ship_date',
    invOemPnStatus: 'inv_oem_pn_status',
    invAssetTagStatus: 'inv_asset_tag_status',
    length: 'length',
    width: 'width',
    height: 'height',
    estimatedWeight: 'estimated_weight',
    weightUnit: 'weight_unit',
    quantity: 'quantity',
    cavities: 'cavities',
  };

  /* ── Live completeness — merges server state + local edits ─────── */
  function computeLiveCompleteness() {
    const merged = { ...tool, ...editedFields };
    const missing = [];
    if (!merged.oem) missing.push('OEM');
    if (!merged.tierLevel && !merged.tier_level) missing.push('Tier Level');
    if (!merged.supplierStatus && !merged.supplier_status) missing.push('Supplier Status');
    // Use local partNumbers state for real-time count
    const pnCount = partNumbers.length;
    if (pnCount < 1) missing.push('at least one Part Number (enter "NPN" if no part number is available)');
    return { is_complete: missing.length === 0, missing_fields: missing };
  }

  /* ── Recon status toggle with close gate ───────────────────────── */
  function handleReconToggle(newStatus) {
    if (newStatus === 'CLOSED') {
      const comp = computeLiveCompleteness();
      if (!comp.is_complete) {
        setReconCloseError(
          comp.missing_fields.length
            ? `Cannot close recon \u2014 missing: ${comp.missing_fields.join(', ')}.`
            : 'Cannot close recon \u2014 missing fields unknown.'
        );
        return;
      }
    }
    // Update local state only — persisted via Save button
    handleFieldChange('reconStatus', newStatus);
    setReconCloseError(null);
  }

  /* ── Persist new location values to ReferenceData ──────────────── */
  async function persistNewLocationValues() {
    const siteId = tool?.siteId || tool?.site_id;
    const locationFields = [
      { camel: 'siteLocation', refKey: 'site_location' },
      { camel: 'detailLocation', refKey: 'detail_location' },
      { camel: 'rowNbr', refKey: 'row_nbr' },
      { camel: 'stackNbr', refKey: 'stack_nbr' },
      { camel: 'rackNbr', refKey: 'rack_nbr' },
      { camel: 'shelfNbr', refKey: 'shelf_nbr' },
      { camel: 'skidNbr', refKey: 'skid_nbr' },
    ];
    for (const { camel, refKey } of locationFields) {
      const val = editedFields[camel];
      if (val && val.trim()) {
        const existing = refOpts[refKey] || [];
        if (!existing.includes(val.trim())) {
          await addRefValue(refKey, val.trim(), siteId);
        }
      }
    }
  }

  /* ── Part Number handlers ────────────────────────────────────────── */
  async function handleAddPN() {
    if (addingPNRef.current || !newPN.trim() || !newPNType) return;
    addingPNRef.current = true;
    try {
      const created = await addPartNumber({
        toolId: tool.id, siteId: toolSiteId, partNumber: newPN.trim(),
        partNumberType: newPNType, partDescription: newPNDesc.trim(), callerEmail,
      });
      setPartNumbers(prev => [...prev, created]);
      setNewPN(''); setNewPNDesc('');
      if (onRefreshCompleteness) onRefreshCompleteness();
    } catch (err) { console.error('Add PN failed:', err); }
    finally { addingPNRef.current = false; }
  }

  async function handleDeletePN(id) {
    try {
      await deletePartNumber({ partNumberId: id, toolId: tool.id, callerEmail });
      setPartNumbers(prev => prev.filter(p => p.id !== id));
      if (onRefreshCompleteness) onRefreshCompleteness();
    } catch (err) { console.error('Delete PN failed:', err); }
  }

  async function handleEditPN(partNumberId, { part_number, part_number_type, part_description }) {
    setPnError(null);
    try {
      const updated = await patchPartNumber({
        partNumberId,
        partNumber: part_number,
        partNumberType: part_number_type,
        partDescription: part_description,
        callerEmail,
      });
      setPartNumbers(prev => prev.map(p => p.id === partNumberId ? updated : p));
    } catch (err) {
      setPnError(err.message);
      throw err;
    }
  }

  /* ── Asset Tag handlers ──────────────────────────────────────────── */
  async function handleAddTag() {
    if (addingTagRef.current || !newTagVal.trim()) return;
    addingTagRef.current = true;
    try {
      const created = await addAssetTag({ toolId: tool.id, siteId: toolSiteId, tagValue: newTagVal.trim(), callerEmail });
      setAssetTags(prev => [...prev, created]);
      setNewTagVal('');
    } catch (err) { console.error('Add tag failed:', err); }
    finally { addingTagRef.current = false; }
  }

  async function handleDeleteTag(tagId) {
    try {
      await deleteAssetTag({ tagId, toolId: tool.id, callerEmail });
      setAssetTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) { console.error('Delete tag failed:', err); }
  }

  async function handleEditTag(tagId, { tag_value }) {
    setAtError(null);
    try {
      const updated = await patchAssetTag({ tagId, tagValue: tag_value, callerEmail });
      setAssetTags(prev => prev.map(t => t.id === tagId ? updated : t));
    } catch (err) {
      setAtError(err.message);
      throw err;
    }
  }

  /* ── Save ─────────────────────────────────────────────────────────── */
  const handleSave = useCallback(async () => {
    if (!tool?.id) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // Convert merged edits to snake_case for update_tool.
      // editedFields (user edits) wins over autoSuggestions on conflict.
      const merged = { ...autoSuggestions, ...editedFields };
      const payload = {};
      for (const [camelKey, value] of Object.entries(merged)) {
        const snakeKey = CAMEL_TO_SNAKE[camelKey];
        if (snakeKey) {
          payload[snakeKey] = value;
        }
      }

      if (Object.keys(payload).length === 0) {
        setSaveMsg('success');
        setTimeout(() => setSaveMsg(null), 2000);
        setSaving(false);
        return;
      }

      const updated = await updateDisposalTool(
        tool.id, payload, callerEmail, currentProject?.id
      );

      // Persist new location values to ReferenceData
      await persistNewLocationValues();

      setEditedFields({});
      setAutoSuggestions({});
      setSaveMsg('success');
      setTimeout(() => setSaveMsg(null), 2000);
      if (onToolUpdate) onToolUpdate({ ...tool, ...autoSuggestions, ...editedFields, ...updated });
    } catch (err) {
      setSaveMsg(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, editedFields, callerEmail, currentProject, onToolUpdate]);

  /* ── Panel order (drag-to-reorder) ──────────────────────────────── */
  const [panelOrder, setPanelOrder] = useState(() => loadPanelOrder(callerEmail));

  useEffect(() => {
    setPanelOrder(loadPanelOrder(callerEmail));
  }, [callerEmail]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPanelOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        const next = arrayMove(prev, oldIdx, newIdx);
        savePanelOrder(callerEmail, next);
        return next;
      });
    }
  }

  function handleResetOrder() {
    localStorage.removeItem(getPanelOrderKey(callerEmail));
    setPanelOrder(DEFAULT_ORDER);
  }

  if (!tool) return null;

  /* ── Derived display values ──────────────────────────────────────── */
  const oemKey = (tool.oem || '').toUpperCase();
  const oemCfg = OEM_CONFIG[oemKey] || { cls: 'dtl-oem-default' };

  const pnDisplay = partNumbers.length > 0
    ? partNumbers.map(p => p.part_number).join(', ')
    : (Array.isArray(tool.part_numbers) && tool.part_numbers.length > 0
      ? tool.part_numbers.join(', ')
      : 'No part numbers');

  /* ── Section content map ─────────────────────────────────────────── */
  const sectionMap = {
    /* ── Panel 1: Tool Identification ─────────────────────────────── */
    tool_identification: (
      <SortableSection key="tool_identification" id="tool_identification" title="Tool Identification" defaultOpen={true}>
        <div className="ddp-grid-2">
          <div className="ddp-field">
            <span className="ddp-label">Tool ID</span>
            <span className="ddp-field-value">{tool.toolId || '—'}</span>
          </div>
          <label className="ddp-field">
            <span className="ddp-label">Alt Tool ID</span>
            <input type="text" className="ddp-input"
              value={displayVal('altToolId') ?? ''}
              onChange={e => handleFieldChange('altToolId', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Tool Type</span>
            <select
              value={displayVal('toolType') ?? ''}
              onChange={e => handleFieldChange('toolType', e.target.value)}
              className="ddp-input"
            >
              <option value="">Select Tool Type</option>
              {(refOpts.tool_type || []).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Material</span>
            <select
              value={displayVal('material') ?? ''}
              onChange={e => handleFieldChange('material', e.target.value)}
              className="ddp-input"
            >
              <option value="">Select Material</option>
              {(refOpts.material || []).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">OEM</span>
            <FixedListSelect
              value={displayVal('oem') ?? ''}
              onChange={v => handleFieldChange('oem', v)}
              options={refOpts.oem || []}
              placeholder="Select OEM"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">List Number</span>
            <input type="text" className="ddp-input"
              value={displayVal('listNumber') ?? ''}
              onChange={e => handleFieldChange('listNumber', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Platform</span>
            <input type="text" className="ddp-input"
              value={displayVal('platform') ?? ''}
              onChange={e => handleFieldChange('platform', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Part Description</span>
            <input type="text" className="ddp-input"
              value={displayVal('partDescription') ?? ''}
              onChange={e => handleFieldChange('partDescription', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Tool Description</span>
            <input type="text" className="ddp-input"
              value={displayVal('toolDescription') ?? ''}
              onChange={e => handleFieldChange('toolDescription', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">CSPC</span>
            <input type="text" className="ddp-input"
              value={displayVal('cspc') ?? ''}
              onChange={e => handleFieldChange('cspc', e.target.value)}
              placeholder="GM asset ID (if applicable)" />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Barcode</span>
            <input type="text" className="ddp-input"
              value={displayVal('barcode') ?? ''}
              onChange={e => handleFieldChange('barcode', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">QR Code</span>
            <input type="text" className="ddp-input"
              value={displayVal('qrCode') ?? ''}
              onChange={e => handleFieldChange('qrCode', e.target.value)} />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 2: Supplier Info ────────────────────────────────────── */
    supplier_info: (
      <SortableSection key="supplier_info" id="supplier_info" title="Supplier Info" defaultOpen={true}>
        <div className="ddp-grid-2">
          <label className="ddp-field">
            <span className="ddp-label">Supplier Status</span>
            <select className="ddp-input"
              value={displayVal('supplierStatus') ?? ''}
              onChange={e => handleFieldChange('supplierStatus', e.target.value)}>
              <option value="">—</option>
              {(refOpts.supplier_status || ['YES=SUBMIT', 'NO=HOLD', 'NPN', 'CAPACITY']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Supplier Confirmed Date</span>
            <input type="date" className="ddp-input"
              value={displayVal('supplierConfirmedDate') ?? ''}
              onChange={e => handleFieldChange('supplierConfirmedDate', e.target.value)} />
          </label>
          <label className="ddp-field ddp-span-2">
            <span className="ddp-label">Supplier Comments</span>
            <textarea className="ddp-input ddp-textarea"
              value={displayVal('supplierComments') ?? ''}
              onChange={e => handleFieldChange('supplierComments', e.target.value)}
              rows={3} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Supplier Confirmed By</span>
            <input type="text" className="ddp-input"
              value={displayVal('supplierConfirmedBy') ?? ''}
              onChange={e => handleFieldChange('supplierConfirmedBy', e.target.value)} />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 3: Reconciliation Status ────────────────────────────── */
    recon_status_panel: (
      <SortableSection key="recon_status_panel" id="recon_status_panel" title="Reconciliation Status" defaultOpen={true}>
        <div className="ddp-grid-2">
          <div className="ddp-field">
            <span className="ddp-label">Recon Status</span>
            <span className="ddp-field-value">{displayVal('reconStatus') || 'OPEN'}</span>
          </div>
          <label className="ddp-field">
            <span className="ddp-label">Preliminary Tool Status</span>
            <select className="ddp-input"
              value={displayVal('preliminaryToolStatus') ?? ''}
              onChange={e => handleFieldChange('preliminaryToolStatus', e.target.value)}>
              <option value="">—</option>
              {(refOpts.preliminary_tool_status || ['ACTIVE', 'DISCON', 'LOW_MVC', 'OBSOLETE', 'SCRAPPED']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
            </select>
          </label>
          <div className="ddp-field">
            <span className="ddp-label">Workflow Status</span>
            <span className="ddp-field-value">{tool.workflowStatus || '—'}</span>
          </div>
          <div className="ddp-field">
            <label className="ddp-checkbox">
              <input type="checkbox"
                checked={displayVal('approvedScrap') ?? false}
                onChange={e => handleFieldChange('approvedScrap', e.target.checked)} />
              <span>Approved Scrap</span>
            </label>
          </div>
          <label className="ddp-field ddp-span-2">
            <span className="ddp-label">Recon Comments</span>
            <textarea className="ddp-input ddp-textarea"
              value={displayVal('reconComments') ?? ''}
              onChange={e => handleFieldChange('reconComments', e.target.value)}
              rows={3} />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 4: Location ─────────────────────────────────────────── */
    location: (
      <SortableSection key="location" id="location" title="Location" defaultOpen={true}>
        <div className="ddp-grid-2">
          <label className="ddp-field">
            <span className="ddp-label">Site Location</span>
            <ComboBox
              value={displayVal('siteLocation') ?? ''}
              onChange={v => handleFieldChange('siteLocation', v)}
              options={refOpts.site_location || []}
              placeholder="Site location"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Section / Detail</span>
            <ComboBox
              value={displayVal('detailLocation') ?? ''}
              onChange={v => handleFieldChange('detailLocation', v)}
              options={refOpts.detail_location || []}
              placeholder="Detail location"
              className="ddp-input"
            />
          </label>
          {/* Editable panel: all five always render. They can load as numbers and
              ComboBox calls .trim() on its value, hence String(). */}
          <label className="ddp-field">
            <span className="ddp-label">Row Nbr</span>
            <ComboBox
              value={String(displayVal('rowNbr') ?? '')}
              onChange={v => handleFieldChange('rowNbr', v)}
              options={refOpts.row_nbr || []}
              placeholder="Row Nbr"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Stack Nbr</span>
            <ComboBox
              value={String(displayVal('stackNbr') ?? '')}
              onChange={v => handleFieldChange('stackNbr', v)}
              options={refOpts.stack_nbr || []}
              placeholder="Stack Nbr"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Rack Nbr</span>
            <ComboBox
              value={String(displayVal('rackNbr') ?? '')}
              onChange={v => handleFieldChange('rackNbr', v)}
              options={refOpts.rack_nbr || []}
              placeholder="Rack Nbr"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Shelf Nbr</span>
            <ComboBox
              value={String(displayVal('shelfNbr') ?? '')}
              onChange={v => handleFieldChange('shelfNbr', v)}
              options={refOpts.shelf_nbr || []}
              placeholder="Shelf Nbr"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Skid Nbr</span>
            <ComboBox
              value={String(displayVal('skidNbr') ?? '')}
              onChange={v => handleFieldChange('skidNbr', v)}
              options={refOpts.skid_nbr || []}
              placeholder="Skid Nbr"
              className="ddp-input"
            />
          </label>
          <label className="ddp-field ddp-span-2">
            <span className="ddp-label">Inventory Comments</span>
            <textarea className="ddp-input ddp-textarea"
              value={displayVal('inventoryComments') ?? ''}
              onChange={e => handleFieldChange('inventoryComments', e.target.value)}
              rows={3} />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 5: Disposal Flags ──────────────────────────────────── */
    disposal_flags: (
      <SortableSection key="disposal_flags" id="disposal_flags" title="Disposal Flags" defaultOpen={false}>
        <div className="ddp-grid-2">
          <div className="ddp-field">
            <label className="ddp-checkbox">
              <input type="checkbox"
                checked={displayVal('capacityFlag') ?? false}
                onChange={e => handleFieldChange('capacityFlag', e.target.checked)} />
              <span>Capacity Flag</span>
            </label>
          </div>
          <div className="ddp-field">
            <label className="ddp-checkbox">
              <input type="checkbox"
                checked={displayVal('npnFlag') ?? false}
                onChange={e => handleFieldChange('npnFlag', e.target.checked)} />
              <span>NPN Flag</span>
            </label>
          </div>
          <div className="ddp-field">
            <label className="ddp-checkbox">
              <input type="checkbox"
                checked={displayVal('found') ?? false}
                onChange={e => handleFieldChange('found', e.target.checked)} />
              <span>Found</span>
            </label>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Missing From Last Import</span>
            <span className="ddp-field-value ddp-bool-display">{fmtBool(tool.missingFromLastImport)}</span>
          </div>
          <label className="ddp-field ddp-span-2">
            <span className="ddp-label">TMG Data Comments</span>
            <textarea className="ddp-input ddp-textarea"
              value={displayVal('tmgDataComments') ?? ''}
              onChange={e => handleFieldChange('tmgDataComments', e.target.value)}
              rows={3} />
          </label>
          <label className="ddp-field ddp-span-2">
            <span className="ddp-label">Retained Reason</span>
            <textarea className="ddp-input ddp-textarea"
              value={displayVal('retainedReason') ?? ''}
              onChange={e => handleFieldChange('retainedReason', e.target.value)}
              rows={3} />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 6: Supply Chain ─────────────────────────────────────── */
    supply_chain: (
      <SortableSection key="supply_chain" id="supply_chain" title="Supply Chain" defaultOpen={false}>
        <div className="ddp-grid-2">
          <label className="ddp-field">
            <span className="ddp-label">Tier Level</span>
            <select className="ddp-input"
              value={displayVal('tierLevel') ?? ''}
              onChange={e => handleFieldChange('tierLevel', e.target.value)}>
              <option value="">—</option>
              {(refOpts.tier_level || ['TIER 1', 'TIER 2', 'TIER 3']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Tier DUNS</span>
            <input type="text" className="ddp-input"
              value={displayVal('tierDuns') ?? ''}
              onChange={e => handleFieldChange('tierDuns', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Ship To Location</span>
            <input type="text" className="ddp-input"
              value={displayVal('shipToLocation') ?? ''}
              onChange={e => handleFieldChange('shipToLocation', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Last Ship Date</span>
            <input type="date" className="ddp-input"
              value={displayVal('lastShipDate') ?? ''}
              onChange={e => handleFieldChange('lastShipDate', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Inv OEM PN Status</span>
            <select className="ddp-input"
              value={displayVal('invOemPnStatus') ?? ''}
              onChange={e => handleFieldChange('invOemPnStatus', e.target.value)}>
              <option value="">—</option>
              {(refOpts.inv_oem_pn_status || ['SEE PIC', 'NO PNR FOUND', 'PNR FOUND']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Inv Asset Tag Status</span>
            <select className="ddp-input"
              value={displayVal('invAssetTagStatus') ?? ''}
              onChange={e => handleFieldChange('invAssetTagStatus', e.target.value)}>
              <option value="">—</option>
              {(refOpts.inv_asset_tag_status || ['SEE PIC', 'NOT TAGGED', 'TAGGED']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
            </select>
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 7: Dimensions & Weight ─────────────────────────────── */
    dimensions_weight: (
      <SortableSection key="dimensions_weight" id="dimensions_weight" title="Dimensions & Weight" defaultOpen={false}>
        <div className="ddp-grid-3">
          <label className="ddp-field">
            <span className="ddp-label">Length</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('length') ?? ''}
              onChange={e => handleFieldChange('length', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Width</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('width') ?? ''}
              onChange={e => handleFieldChange('width', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Height</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('height') ?? ''}
              onChange={e => handleFieldChange('height', e.target.value)} />
          </label>
        </div>
        <div className="ddp-grid-3">
          <label className="ddp-field">
            <span className="ddp-label">Estimated Weight</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('estimatedWeight') ?? ''}
              onChange={e => handleFieldChange('estimatedWeight', e.target.value)} />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Weight Unit</span>
            <select className="ddp-input"
              value={displayVal('weightUnit') ?? ''}
              onChange={e => handleFieldChange('weightUnit', e.target.value)}>
              <option value="">—</option>
              <option value="LBS">LBS</option>
              <option value="KG">KG</option>
              <option value="TONS">TONS</option>
            </select>
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Quantity</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('quantity') ?? ''}
              onChange={e => handleFieldChange('quantity', e.target.value)}
              step="1" />
          </label>
          <label className="ddp-field">
            <span className="ddp-label">Cavities</span>
            <input type="number" className="ddp-input ddp-input-number"
              value={displayVal('cavities') ?? ''}
              onChange={e => handleFieldChange('cavities', e.target.value)}
              step="1" />
          </label>
        </div>
      </SortableSection>
    ),

    /* ── Panel 8: Part Numbers ─────────────────────────────────────── */
    part_numbers: (
      <SortableSection key="part_numbers" id="part_numbers" title="Part Numbers" defaultOpen={false}>
        <PartNumbersList
          partNumbers={sortedPartNumbers}
          error={pnError}
          onAdd={handleAddPN}
          onEdit={handleEditPN}
          onDelete={handleDeletePN}
          newPn={newPN}
          setNewPn={setNewPN}
          newPnType={newPNType}
          setNewPnType={setNewPNType}
          newPnDesc={newPNDesc}
          setNewPnDesc={setNewPNDesc}
          partNumberTypeOptions={refOpts.part_number_type}
        />
      </SortableSection>
    ),

    /* ── Panel 9: Asset Tags ──────────────────────────────────────── */
    asset_tags: (
      <SortableSection key="asset_tags" id="asset_tags" title="Asset Tags" defaultOpen={false}>
        <AssetTagsList
          assetTags={assetTags}
          error={atError}
          onAdd={handleAddTag}
          onEdit={handleEditTag}
          onDelete={handleDeleteTag}
          newTag={newTagVal}
          setNewTag={setNewTagVal}
        />
      </SortableSection>
    ),

    /* ── Photos ───────────────────────────────────────────────────── */
    photos: (
      <SortableSection key="photos" id="photos" title="Photos" defaultOpen={false}>
        <PhotoGalleryPanel toolId={tool.id} callerEmail={callerEmail} />
      </SortableSection>
    ),

    /* ── Panel 11: System Fields ──────────────────────────────────── */
    system_fields: (
      <SortableSection key="system_fields" id="system_fields" title="System Fields" defaultOpen={false}>
        <div className="ddp-grid-2">
          <div className="ddp-field">
            <span className="ddp-label">Created At</span>
            <span className="ddp-field-value">{fmtDatetime(tool.createdAt) || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Created By</span>
            <span className="ddp-field-value">{tool.createdBy || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Updated At</span>
            <span className="ddp-field-value">{fmtDatetime(tool.updatedAt) || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Updated By</span>
            <span className="ddp-field-value">{tool.updatedBy || '—'}</span>
          </div>
        </div>
      </SortableSection>
    ),
  };

  return (
    <div className="ddp-panel">
      {/* ── Sticky top: header + save ─────────────────────────────────── */}
      <div className="ddp-sticky-top">
        <div className="ddp-header-strip">
          <div className="ddp-header-top">
            <span className="ddp-header-label">Tool ID:</span>
            <h2 className="ddp-tool-id">{tool.toolId || '—'}</h2>
            {tool.altToolId && (
              <span className="ddp-alt-id">{tool.altToolId}</span>
            )}
          </div>
          <div className="ddp-header-meta">
            <span className="ddp-header-label">Site:</span>
            <span className="ddp-meta-text">{tool.site_name || tool.supplier_name || '—'}</span>
          </div>
          <div className="ddp-header-meta">
            <span className="ddp-header-label">OEM:</span>
            {tool.oem
              ? <span className={`dtl-badge ${oemCfg.cls}`}>{tool.oem}</span>
              : <span className="ddp-meta-text">—</span>
            }
            <span className="ddp-header-label ddp-header-label-gap">Status:</span>
            {tool.workflowStatus
              ? <span className="ddp-badge ddp-badge-workflow">{tool.workflowStatus}</span>
              : <span className="ddp-meta-text">—</span>
            }
          </div>
          <div className="ddp-header-meta">
            <span className="ddp-header-label">Parts:</span>
            <span className="ddp-pn-list">{pnDisplay === 'No part numbers' ? '—' : pnDisplay}</span>
          </div>
          <div className="ddp-header-meta">
            <span className="ddp-header-label">Die Op:</span>
            <span className="ddp-meta-text">{tool.dieOp || '—'}</span>
          </div>
          <div className="ddp-header-meta">
            <span className="ddp-header-label">ToolSight ID:</span>
            <span className="ddp-toolsight-id">{tool.id || '—'}</span>
          </div>

          {/* ── Recon status toggle ──────────────────────────────────── */}
          <div className="ddp-header-fields-row">
            <div className="ddp-header-field">
              <label className="ddp-label">Recon Status</label>
              <div className="ddp-recon-toggle">
                <button
                  type="button"
                  className={`ddp-recon-btn ${(displayVal('reconStatus') || 'OPEN') === 'OPEN' ? 'active open' : ''}`}
                  onClick={() => handleReconToggle('OPEN')}
                >Open</button>
                <button
                  type="button"
                  className={`ddp-recon-btn ${displayVal('reconStatus') === 'CLOSED' ? 'active closed' : ''}`}
                  onClick={() => handleReconToggle('CLOSED')}
                >Closed</button>
              </div>
              {reconCloseError && (
                <div className="ddp-recon-error">{reconCloseError}</div>
              )}
            </div>
          </div>
        </div>

        {/* ── Save bar ────────────────────────────────────────────────── */}
        <div className="ddp-save-bar">
          <button
            className="ddp-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {isDirty && (
            <button
              className="ddp-btn-cancel"
              onClick={() => setEditedFields({})}
              disabled={saving}
              type="button"
            >
              Cancel
            </button>
          )}
          {saveMsg === 'success' && (
            <span className="ddp-save-ok">Changes saved.</span>
          )}
          {saveMsg && saveMsg !== 'success' && (
            <span className="ddp-save-err">{saveMsg}</span>
          )}
          {isAdmin && (
            <button type="button" className="ddp-btn-delete" onClick={openDeleteConfirm} disabled={saving || deleting}>
              Delete tool
            </button>
          )}
        </div>
      </div>

      {/* ── Sortable sections ──────────────────────────────────────────── */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={panelOrder} strategy={verticalListSortingStrategy}>
          {panelOrder.map(id => sectionMap[id] || null)}
        </SortableContext>
      </DndContext>

      <button className="ddp-reset-order" onClick={handleResetOrder} type="button">
        Reset panel order
      </button>

      {/* Delete confirm — mounted as the LAST child of div.ddp-panel, deliberately
          NOT inside .ddp-sticky-top or next to its button. .ddp-sticky-top is
          position:sticky + z-index:20 (a stacking context) and ModalShell has no
          portal, so a modal mounted in there paints at level 20 — BELOW the
          topbar (50) and rail (100). Do not move it back up. */}
      <ModalShell
        isOpen={confirmOpen}
        onClose={closeDeleteConfirm}
        title={deleteError ? "Couldn't delete this tool" : 'Delete this tool?'}
        footer={
          deleteError && isDeleteRetryFutile(deleteError) ? (
            <button type="button" className="ddp-btn-cancel" onClick={closeDeleteConfirm}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="ddp-btn-cancel" onClick={closeDeleteConfirm} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="ddp-btn-delete-confirm" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete tool'}
              </button>
            </>
          )
        }
      >
        {deleteError ? (
          <>
            {/* The backend's `error` string is written for the operator: rendered
                verbatim, never paraphrased or rebuilt from `reason`. No .status
                means fetch rejected before any Response existed — that message is
                the browser's ("Failed to fetch"), not the backend's, so replacing
                it is not a paraphrase of anything the operator was meant to read. */}
            <div className="ddp-save-err">
              {deleteError.status
                ? deleteError.message
                : "Couldn't reach the server. Check your connection and try again."}
            </div>
            {STALE_LIST_REASONS.has(deleteError.body?.reason) && (
              <p className="ddp-record-desc">This list is out of date — refresh to see the current tools.</p>
            )}
            {/* A refusal leaves editedFields intact, and a retryable one keeps the
                Delete tool button — same condition as the footer, so the warning
                shows exactly when a retry could still discard the edits. */}
            {isDirty && !isDeleteRetryFutile(deleteError) && (
              <p className="ddp-delete-unsaved-warn">
                You have unsaved changes on this tool. Deleting discards them.
              </p>
            )}
          </>
        ) : (
          // Intro copy only before a refusal: it describes what a successful
          // delete does, and once a refusal renders, no delete has happened.
          <>
            <p className="ddp-field-value">
              This hides the tool from every list. Nothing is destroyed — photos, part numbers and asset tags stay attached, and an ADMIN can restore it from the Recycle Bin.
            </p>
            {/* isDirty, not editedFields + autoSuggestions: auto-applied values
                are not the operator's work, and warning about them would train
                people to ignore this line. */}
            {isDirty && (
              <p className="ddp-delete-unsaved-warn">
                You have unsaved changes on this tool. Deleting discards them.
              </p>
            )}
          </>
        )}
        {/* Raw `tool` fields only, never displayVal: this describes what is in the
            database, not unsaved edits that are about to be discarded. The UI now
            says this out loud — see the isDirty conditional above. */}
        <div className="ddp-grid-2">
          <div className="ddp-field">
            <span className="ddp-label">ToolSight ID</span>
            <span className="ddp-field-value">{tool.id || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Tool ID</span>
            <span className="ddp-field-value">{tool.toolId || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Die Op</span>
            <span className="ddp-field-value">{tool.dieOp || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Tool Type</span>
            <span className="ddp-field-value">{tool.toolType || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Part Description</span>
            <span className="ddp-field-value">{tool.partDescription || '—'}</span>
          </div>
          <div className="ddp-field">
            <span className="ddp-label">Site</span>
            <span className="ddp-field-value">{tool.site_name || tool.supplier_name || '—'}</span>
          </div>
        </div>
      </ModalShell>
    </div>
  );
}
